import React, { useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useBoolean } from 'minimal-shared/hooks';

import { Button, Grid2 as Grid, CircularProgress } from '@mui/material';

import { paths } from 'src/routes/paths';

import { mockDefaultFilters } from 'src/hooks/useFilters';

import { CONFIG } from 'src/global-config';
import { FiltersProvider, useFiltersContext } from 'src/providers/FiltersProvider';

import { Page } from 'src/components/Page/Page';
import { EmptyState } from 'src/components/EmptyState';
import { FilterForm } from 'src/components/FilterPanel/FilterForm';
import { FilterPanel } from 'src/components/FilterPanel/FilterPanel';
import { FiltersResults } from 'src/components/FilterPanel/FiltersResult';
import { FiltersToolbar } from 'src/components/FilterPanel/FiltersToolbar';

import { REPORT_CUSTOMER_TYPES } from '../common/consts';
import { Promotion } from './layout/Promotion/Promotion';
import { downloadReport } from '../common/downloadReport';
import { generatePromotionsJSON } from './utils/generatePromotionsReportJSON';
import {
  usePromotionsSummaryReport,
  PromotionsSummaryReportProvider,
} from './provider/PromotionsSummaryReportProvider';

const metadata = { title: `Insights | Promotions - ${CONFIG.appName}` };

function PromotionsSummaryConsumer() {
  const { loading, mutate, data, error } = usePromotionsSummaryReport();
  const { resetFilters, updateFilters, filters } = useFiltersContext();
  const filtersPanel = useBoolean();

  const onExport = useCallback(() => {
    if (loading || !data) {
      return;
    }

    const json = generatePromotionsJSON({
      data: data.reports,
    });

    downloadReport({
      data: json,
    });
  }, [data, loading]);

  return (
    <>
      <Helmet>
        <title> {metadata.title}</title>
      </Helmet>
      <Page
        title="Promotions Summary"
        breadcrumbs={[
          { name: 'Insights', href: paths.insights.root },
          { name: 'Promotions', href: paths.insights.promotions.root },
        ]}
      >
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <FiltersResults
              slots={{
                date: false,
                location: false,
                source: true,
                customerType: true,
              }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FiltersToolbar
              onOpen={filtersPanel.onTrue}
              loading={loading}
              onReloadData={() => mutate()}
              onExport={onExport}
              slots={{
                quickFilters: {
                  label: '',
                  onUpdateFilters: updateFilters,
                  quickFilters: [],
                },
              }}
            />
          </Grid>
          {loading && (
            <Grid
              size={{ xs: 12 }}
              sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
              <EmptyState
                hideIcon
                title="Loading..."
                description="Please wait while we load the data, it may take up to a minute."
                action={<CircularProgress sx={{ mt: 1 }} size={24} />}
              />
            </Grid>
          )}
          {error && (
            <Grid
              size={{ xs: 12 }}
              sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
              <EmptyState
                title="Error"
                description="An error occurred while loading the data. Please try again later."
                action={<Button onClick={() => mutate()}>Try Again</Button>}
              />
            </Grid>
          )}
          {!loading && (
            <>
              {data?.reports.map((report) => <Promotion key={report.eventId} promotion={report} />)}
            </>
          )}
        </Grid>
        <FilterPanel
          open={filtersPanel.value}
          onClose={filtersPanel.onToggle}
          onResetFilters={resetFilters}
          renderFilterForm={() => (
            <FilterForm
              currentFilters={filters}
              onUpdateFilters={updateFilters}
              onResetFilters={resetFilters}
              allowedSources={['app']}
              allowFilterByStore={false}
              allowFilterByDate={false}
              allowedCustomerTypes={REPORT_CUSTOMER_TYPES.map((t) => t.value)}
            />
          )}
        />
      </Page>
    </>
  );
}

export default function PromotionsSummary() {
  return (
    <FiltersProvider
      defaultFilters={{
        ...mockDefaultFilters,
        source: 'app',
      }}
    >
      <PromotionsSummaryReportProvider>
        <PromotionsSummaryConsumer />
      </PromotionsSummaryReportProvider>
    </FiltersProvider>
  );
}
